import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import '../styles/HomeScreenDemo.css';

const mapContainerStyle = {
  width: '100%',
  height: '500px'
};

const center = {
  lat: 40.7580,
  lng: -73.9855
};

const HomeScreenDemo = () => {
  const [bathrooms, setBathrooms] = useState([]);
  const [selectedBathroom, setSelectedBathroom] = useState(null);
  const [viewMode, setViewMode] = useState('map');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [confirmedId, setConfirmedId] = useState(null);

  // Mock data with high-quality bathroom images
  useEffect(() => {
    const demoBathrooms = [
      {
        _id: '1',
        name: 'Times Square Premium Restroom',
        address: '1540 Broadway, New York, NY 10036',
        coordinates: { lat: 40.7579, lng: -73.9855 },
        price: 2.50,
        photoUrls: [
          'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
        ],
        features: ['ADA Accessible', 'Baby Changing', 'Premium Soap'],
        code: '777',
        distance: `${Math.floor(Math.random() * 201) + 100} ft`
      },
      {
        _id: '2',
        name: 'Port Authority Restrooms',
        address: '625 8th Ave, New York, NY 10018',
        coordinates: { lat: 40.7565, lng: -73.9909 },
        price: 1.00,
        photoUrls: [
          'https://images.unsplash.com/photo-1551524559-8af4e6624178?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
        ],
        features: ['High Capacity', '24/7 Access'],
        code: '888',
        distance: `${Math.floor(Math.random() * 201) + 100} ft`
      },
      {
        _id: '3',
        name: 'Bryant Park Restroom',
        address: 'W 40th St, New York, NY 10018',
        coordinates: { lat: 40.7536, lng: -73.9832 },
        price: 0.00,
        photoUrls: [
          'https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'
        ],
        features: ['Free', 'Park Setting', 'Seasonal Hours'],
        code: '999',
        distance: `${Math.floor(Math.random() * 201) + 100} ft`
      }
    ];
    setBathrooms(demoBathrooms);
  }, []);

  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'map' ? 'list' : 'map');
  };

  const getDirections = (bathroom) => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${bathroom.coordinates.lat},${bathroom.coordinates.lng}&travelmode=walking`
    );
  };

  const handleConfirm = (id) => {
    setConfirmedId(id);
  };

  return (
    <div className="home-container">
      <div className="demo-header">
        <h1>Pay2Pee Demo</h1>
        <p>Find clean, affordable restrooms in Times Square area</p>
      </div>
      
      <div className="view-toggle">
        <button 
          onClick={toggleViewMode}
          className={viewMode === 'map' ? 'active' : ''}
        >
          Map View
        </button>
        <button 
          onClick={toggleViewMode}
          className={viewMode === 'list' ? 'active' : ''}
        >
          List View
        </button>
      </div>
      
      {viewMode === 'map' ? (
        <div className="map-container">
          {mapError ? (
            <div className="map-error">
              <p>Map failed to load. Please check your API key.</p>
              <button onClick={() => setMapError(null)}>Retry</button>
            </div>
          ) : (
            <LoadScript
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              libraries={['places']}
              onLoad={() => setMapLoaded(true)}
              onError={(error) => setMapError(error.message)}
            >
              {mapLoaded && (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={center}
                  zoom={15}
                  options={{
                    mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
                    streetViewControl: false,
                    mapTypeControl: false,
                    styles: [
                      {
                        featureType: "poi",
                        stylers: [{ visibility: "off" }]
                      }
                    ]
                  }}
                >
                  {bathrooms.map(bathroom => (
                    <Marker
                      key={bathroom._id}
                      position={bathroom.coordinates}
                      onClick={() => {
                        setSelectedBathroom(bathroom);
                        setConfirmedId(null); // Reset confirmation when selecting new bathroom
                      }}
                      icon={{
                        url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                      }}
                    />
                  ))}
                  
                  {selectedBathroom && (
                    <InfoWindow
                      position={selectedBathroom.coordinates}
                      onCloseClick={() => {
                        setSelectedBathroom(null);
                        setConfirmedId(null);
                      }}
                    >
                      <div className="info-window">
                        <h3>{selectedBathroom.name}</h3>
                        <p>{selectedBathroom.address}</p>
                        <p className="price">${selectedBathroom.price.toFixed(2)} per visit</p>
                        <p className="distance">{selectedBathroom.distance} away</p>
                        <div className="features">
                          {selectedBathroom.features.map((feature, i) => (
                            <span key={i} className="feature-tag">{feature}</span>
                          ))}
                        </div>
                        {confirmedId === selectedBathroom._id ? (
                          <>
                            <p className="access-code">Access code: {selectedBathroom.code}</p>
                            <button 
                              className="directions-button"
                              onClick={() => getDirections(selectedBathroom)}
                            >
                              Get Directions
                            </button>
                          </>
                        ) : (
                          <button 
                            className="confirm-button"
                            onClick={() => handleConfirm(selectedBathroom._id)}
                          >
                            Confirm
                          </button>
                        )}
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              )}
            </LoadScript>
          )}
        </div>
      ) : (
        <div className="list-container">
          {bathrooms.map(bathroom => (
            <div
              key={bathroom._id}
              className="bathroom-card"
              onClick={() => {
                setSelectedBathroom(bathroom);
                setConfirmedId(null); // Reset confirmation when selecting new bathroom
              }}
            >
              <div className="bathroom-image">
                <img 
                  src={bathroom.photoUrls[0]} 
                  alt={`${bathroom.name} restroom`}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x200?text=Bathroom+Image';
                  }}
                />
              </div>
              <div className="bathroom-info">
                <h3>{bathroom.name}</h3>
                <p className="address">{bathroom.address}</p>
                <p className="price">${bathroom.price.toFixed(2)} per visit</p>
                <p className="distance">{bathroom.distance} away</p>
                <div className="features">
                  {bathroom.features.slice(0, 2).map((feature, i) => (
                    <span key={i} className="feature-tag">{feature}</span>
                  ))}
                  {bathroom.features.length > 2 && (
                    <span className="feature-tag">+{bathroom.features.length - 2} more</span>
                  )}
                </div>
                {confirmedId === bathroom._id ? (
                  <>
                    <p className="access-code">Access code: {bathroom.code}</p>
                    <button 
                      className="directions-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        getDirections(bathroom);
                      }}
                    >
                      Get Directions
                    </button>
                  </>
                ) : (
                  <button 
                    className="confirm-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirm(bathroom._id);
                    }}
                  >
                    Confirm
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="demo-note">
        <p>This is a demo version with sample data. In the full app, locations would update in real-time.</p>
      </div>
    </div>
  );
};

export default HomeScreenDemo;